import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext.jsx';
import { useSocket } from './Context.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Send, Search, X, UserPlus, Maximize2, Smile, Trash2, Users, MessageSquare, Check, CheckCheck
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PostModal from './PostModal.jsx';
import './MessagesWidget.css';
import { getGroupUnreadCount, isGroupUnread, markGroupAsRead } from '../utils/messageBadge.js';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
const BASE_URL = `${GREENWICH_BACKEND_URL}`;
const getImageUrl = (path) => {
  if (!path || path === 'null' || path === 'undefined') return '';
  if (path.startsWith('http')) return path;
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `${BASE_URL}${clean}`;
};

function MessagesWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // 'direct' | 'groups'
  const [activeTab, setActiveTab] = useState('direct');

  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);

  const [activeConversation, setActiveConversation] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [followedUsers, setFollowedUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sharePost, setSharePost] = useState(null);
  const [openPostModalId, setOpenPostModalId] = useState(null);

  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [emojiGroup, setEmojiGroup] = useState('recent');
  const [recentEmojis, setRecentEmojis] = useState([]);
  const textareaRef = useRef(null);
  const emojiPanelRef = useRef(null);
  const emojiButtonRef = useRef(null);

  const messagesEndRef = useRef(null);
  const widgetRef = useRef(null);

  const activeChat = activeConversation || activeGroup;

  // Check if current page is homepage
  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  // ─── Helpers ───────────────────────────────────────────────────
  const getOtherParticipant = (conv) => {
    if (!conv) return null;
    return (conv.participants || []).find(p => p._id !== user._id);
  };

  const getGroupName = (group) => {
    if (group.name) return group.name;
    const others = (group.members || [])
      .filter(p => (p._id || p) !== user._id)
      .map(p => p.organizationName || p.name || t('messagesWidget.unknown'));
    return others.slice(0, 3).join(', ') + (others.length > 3 ? ` +${others.length - 3}` : '');
  };

  const getMemberCount = (group) => group.membersCount || group.members?.length || 0;

  const getSenderName = (message) =>
    message.sender?.organizationName || message.sender?.name || '';

  const isGroupMember = (group) => {
    if (!group || !user) return false;
    const inMembers = group.members?.some(m => (typeof m === 'object' ? m._id : m) === user._id);
    const isCreator = group.createdBy === user._id ||
      (typeof group.createdBy === 'object' && group.createdBy._id === user._id);
    return inMembers || isCreator || group.isMember;
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const diff = Date.now() - d;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  // ─── Data loading ───────────────────────────────────────────────
  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${BASE_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
        updateUnreadCount(data.conversations || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${BASE_URL}/api/community/my-groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const memberGroups = [...(data.createdGroups || []), ...(data.memberGroups || [])];
        setGroups(memberGroups);
        updateUnreadCount(conversations, memberGroups);
      }
    } catch (e) { console.error(e); }
  };

  const loadFollowedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${BASE_URL}/api/users/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFollowedUsers(data.following || data || []);
      }
    } catch (e) { console.error(e); }
  };

  const updateUnreadCount = (convs = conversations, groupList = groups) => {
    const directUnread = convs.reduce((s, c) => s + (c.unreadCount || 0), 0);
    const groupUnread = getGroupUnreadCount(groupList, user?._id);
    setUnreadCount(directUnread + groupUnread);
  };

  const loadMessages = async (id, isGroup = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = isGroup
        ? `${BASE_URL}/api/community/groups/${id}/messages`
        : `${BASE_URL}/api/conversations/${id}/messages`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const markAsRead = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BASE_URL}/api/conversations/${conversationId}/read`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      loadConversations();
    } catch (e) { console.error(e); }
  };

  // ─── Effects ────────────────────────────────────────────────────
  useEffect(() => {
    if (user && !isHomePage) { 
      loadConversations(); 
      loadGroups(); 
      loadFollowedUsers(); 
    }
  }, [user, isHomePage]);

  useEffect(() => {
    updateUnreadCount(conversations, groups);
  }, [conversations, groups, user?._id]);

  // Without this, the widget only ever loaded conversations once on mount — a new message
  // arriving while it's closed/idle never touched the unread badge or conversation preview
  // until the component happened to remount (e.g. a full page navigation).
  useEffect(() => {
    if (!socket || !user) return;
    const handleNewMessage = (data) => {
      loadConversations();
      if (activeConversation && data?.conversationId === activeConversation._id && isOpen) {
        loadMessages(activeConversation._id, false);
      }
    };
    socket.on('new-conversation-message', handleNewMessage);
    socket.on('new-message', handleNewMessage);
    return () => {
      socket.off('new-conversation-message', handleNewMessage);
      socket.off('new-message', handleNewMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user, activeConversation, isOpen]);

  useEffect(() => {
    const handleReadStateChange = () => updateUnreadCount(conversations, groups);
    window.addEventListener('groupMessageReadStateChanged', handleReadStateChange);
    return () => window.removeEventListener('groupMessageReadStateChanged', handleReadStateChange);
  }, [conversations, groups, user?._id]);

  useEffect(() => {
    if (activeConversation && isOpen) {
      loadMessages(activeConversation._id, false);
      markAsRead(activeConversation._id);
    }
  }, [activeConversation, isOpen]);

  useEffect(() => {
    if (activeGroup && isOpen) {
      markGroupAsRead(activeGroup._id);
      updateUnreadCount();
      loadMessages(activeGroup._id, true);
    }
  }, [activeGroup, isOpen]);

  useEffect(() => { if (isOpen) scrollToBottom(); }, [messages, isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target) &&
          !e.target.closest('.messages-widget-button')) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ─── Send message ───────────────────────────────────────────────
  const sendMessage = async () => {
    if (!newMessage.trim() && !sharePost) return;
    if (!activeChat) return;
    try {
      const token = localStorage.getItem('token');
      const isGroup = !!activeGroup;
      const url = isGroup
        ? `${BASE_URL}/api/community/groups/${activeGroup._id}/messages`
        : `${BASE_URL}/api/conversations/${activeConversation._id}/messages`;

      const body = isGroup
        ? JSON.stringify({ content: newMessage.trim() })
        : JSON.stringify({
            content: newMessage.trim() ||
              t('messagesWidget.sharedPostMessage', { name: sharePost?.author?.name || sharePost?.author?.organizationName || t('messagesWidget.unknown') }),
            metadata: sharePost ? {
              type: 'shared_post', sharedPost: true,
              postId: sharePost._id,
              postUrl: `${window.location.origin}/post/${sharePost._id}`,
              postAuthorId: sharePost.author?._id,
              postAuthorName: sharePost.author?.name || sharePost.author?.organizationName,
              postAuthorImage: sharePost.author?.profileImage,
              postContent: sharePost.content,
              postImage: sharePost.images?.[0] || null,
              postImages: sharePost.images || [],
              sharedBy: { _id: user?._id, name: user?.organizationName || user?.name, profileImage: user?.profileImage }
            } : null
          });

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        setSharePost(null);
        isGroup ? loadGroups() : loadConversations();
      }
    } catch (e) { console.error(e); }
  };

  // ─── Start DM ───────────────────────────────────────────────────
  const startConversation = async (targetUser) => {
    if (!targetUser) return;
    try {
      const token = localStorage.getItem('token');
      const existing = conversations.find(c =>
        !c.isGroup && c.participants?.find(p => p._id === (targetUser._id || targetUser.user?._id))
      );
      if (existing) {
        setActiveConversation(existing); setActiveGroup(null);
        setActiveTab('direct'); setIsOpen(true); return;
      }
      const res = await fetch(`${BASE_URL}/api/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ participantIds: [targetUser._id || targetUser.user?._id], isGroup: false })
      });
      if (res.ok) {
        const data = await res.json();
        setActiveConversation(data.conversation); setActiveGroup(null);
        setConversations(prev => [data.conversation, ...prev]);
        setShowNewChatModal(false); setIsOpen(true);
      }
    } catch (e) { console.error(e); }
  };

  // ─── Delete message ─────────────────────────────────────────────
  const handleDeleteMessage = async (messageId) => {
    if (!messageId || !window.confirm(t('messagesWidget.confirmDeleteMessage'))) return;
    const backup = messages;
    setMessages(prev => prev.filter(m => m._id !== messageId));
    try {
      const token = localStorage.getItem('token');
      const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      let res;
      if (activeGroup?._id) {
        res = await fetch(`${BASE_URL}/api/community/groups/${activeGroup._id}/messages/${messageId}`, { method: 'DELETE', headers: h });
      } else if (activeConversation?._id) {
        res = await fetch(`${BASE_URL}/api/conversations/${activeConversation._id}/messages/${messageId}`, { method: 'DELETE', headers: h });
      }
      if (res?.ok) return;
      throw new Error('Delete failed');
    } catch (e) { console.error(e); setMessages(backup); }
  };

  const handleBack = () => { setActiveConversation(null); setActiveGroup(null); setMessages([]); };

  // ─── Emoji picker ────────────────────────────────────────────────
  const insertEmoji = (emoji) => {
    if (!emoji) return;
    const ta = textareaRef.current;
    if (ta && document.activeElement === ta && typeof ta.selectionStart === 'number') {
      const s = ta.selectionStart, e2 = ta.selectionEnd;
      setNewMessage(`${newMessage.slice(0, s)}${emoji}${newMessage.slice(e2)}`);
      setTimeout(() => { try { ta.focus(); ta.setSelectionRange(s + emoji.length, s + emoji.length); } catch {} }, 0);
      return;
    }
    setNewMessage(p => `${p}${emoji}`);
  };

  const handleEmojiPick = (emoji) => {
    setRecentEmojis(prev => {
      const c = Array.isArray(prev) ? prev.filter(e => e !== emoji) : [];
      return [emoji, ...c].slice(0, 12);
    });
    insertEmoji(emoji);
    setEmojiPickerOpen(false);
  };

  useEffect(() => {
    if (!emojiPickerOpen) return;
    const handler = (e) => {
      if (emojiPanelRef.current?.contains(e.target)) return;
      if (emojiButtonRef.current?.contains(e.target)) return;
      setEmojiPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emojiPickerOpen]);

  const EMOJI_GROUPS = {
    recent: { label: t('messagesWidget.recent') },
    smileys: { label: t('messagesWidget.smileys'), emojis: ['😀','😁','😂','🤣','😊','😎','😍','😘','😜','🤩','🥳','😇'] },
    hearts:  { label: t('messagesWidget.hearts'),  emojis: ['❤️','🧡','💛','💚','💙','💜','🤍','🖤','💖','💘','💕','💞'] },
    hands:   { label: t('messagesWidget.hands'),   emojis: ['👏','🙌','🤝','🙏','✌️','🤞','👌','🫶','💪','🤲','👐','🧡'] },
    animals: { label: t('messagesWidget.animals'), emojis: ['🐶','🐱','🐻','🐼','🐨','🦊','🐸','🐵','🦄','🐯','🐮','🐷'] },
    food:    { label: t('messagesWidget.food'),    emojis: ['🍕','🍔','🌮','🍣','🍜','🍩','🍪','🍫','☕','🍉','🥑','🍞'] },
    travel:  { label: t('messagesWidget.travel'),  emojis: ['✈️','🚗','🚆','🗺️','🏝️','🏙️','🌋','🧳','🚀','🚶','🛶','🏕️'] },
    symbols: { label: t('messagesWidget.symbols'), emojis: ['✨','🔥','💫','⭐','⚡','❄️','🌙','☀️','☂️','🎉','🎈','📌'] },
    weather: { label: t('messagesWidget.weather'), emojis: ['☀️','🌤️','☁️','🌧️','⛈️','🌩️','🌈','❄️','🍃','🌾','🌀','🧊'] },
  };
  const emojiGroupOrder = ['recent','smileys','hearts','hands','animals','food','travel','symbols','weather'];
  const defaultRecent  = ['😊','😍','😂','❤️','🙏','✨','🎉','👍'];
  const currentEmojiList = emojiGroup === 'recent'
    ? (recentEmojis.length > 0 ? recentEmojis : defaultRecent).slice(0, 20)
    : EMOJI_GROUPS[emojiGroup]?.emojis || [];

  const EmojiPicker = ({ variant = 'panel' }) => (
    <div ref={variant === 'panel' ? emojiPanelRef : null}
      className={`widget-emoji-picker ${variant === 'modal' ? 'widget-emoji-picker-modal' : ''}`}>
      <div className="widget-emoji-groups">
        {emojiGroupOrder.map(gid => (
          <button key={gid} type="button"
            className={`widget-emoji-group-btn ${emojiGroup === gid ? 'active' : ''}`}
            onClick={() => setEmojiGroup(gid)}>
            {EMOJI_GROUPS[gid]?.label || t('messagesWidget.recent')}
          </button>
        ))}
      </div>
      <div className="widget-emoji-grid">
        {currentEmojiList.map(em => (
          <button key={em} type="button" className="widget-emoji-btn" onClick={() => handleEmojiPick(em)}>
            {em}
          </button>
        ))}
      </div>
    </div>
  );

  // ─── Share post ─────────────────────────────────────────────────
  const handleSharePostToMessages = (post) => {
    setSharePost(post);
    setIsOpen(true);
    if (!activeConversation) setShowNewChatModal(true);
  };
  useEffect(() => {
    window.sharePostToMessages = handleSharePostToMessages;
    return () => delete window.sharePostToMessages;
  }, []);

  if (!user) return null;

  // Don't render on homepage
  if (isHomePage) {
    return null;
  }

  // ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @media (max-width: 992px) { .messages-widget-button { display: none !important; } }

        .widget-tab-bar {
          display: flex;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .widget-tab-btn {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
          padding: 9px 6px; font-size: 12px; font-weight: 600; cursor: pointer;
          border: none; background: transparent; color: #64748b; transition: all .15s;
          border-bottom: 2px solid transparent;
        }
        .widget-tab-btn.active { color: #32614d; border-bottom-color: #32614d; background: #f0fdf4; }
        .widget-tab-btn:hover:not(.active) { background: #f8fafc; color: #32614d; }

        .widget-conv-group-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: #d1fae5; display: flex; align-items: center;
          justify-content: center; flex-shrink: 0; color: #32614d;
          border: 2px solid #a7f3d0;
        }
        .widget-group-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: #d1fae5; display: flex; align-items: center;
          justify-content: center; flex-shrink: 0; color: #32614d;
          border: 2px solid #a7f3d0;
        }
        .widget-group-sender-name {
          font-size: 11px; font-weight: 600; color: #32614d;
          margin-bottom: 2px; padding-left: 1px;
        }
      `}</style>

      {/* ── Floating button ── */}
      <button className="messages-widget-button" onClick={() => setIsOpen(!isOpen)} title={t('messagesWidget.messages')}>
        <img src="/nkarner/logo - Copy.png" alt={t('messagesWidget.messages')} className="message-logo-icon" />
        {unreadCount > 0 && <span className="message-notification-badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="messages-widget" ref={widgetRef}>

          {/* Header */}
          <div className="messages-widget-header">
            <h3>{t('messagesWidget.messages')}</h3>
            <div className="widget-header-actions">
              <button className="widget-action-btn"
                onClick={() => { setShowNewChatModal(true); setActiveConversation(null); setActiveGroup(null); }}
                title={t('messagesWidget.newMessage')}><UserPlus size={18} /></button>
              <button className="widget-action-btn"
                onClick={() => { navigate('/messages'); setIsOpen(false); }}
                title={t('messagesWidget.fullScreen')}><Maximize2 size={18} /></button>
              <button className="widget-action-btn" onClick={() => setIsOpen(false)} title={t('messagesWidget.close')}>
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ══ Active chat ══════════════════════════════════════════ */}
          {activeChat ? (
            <>
              {/* Chat header */}
              <div className="widget-chat-header">
                <button className="widget-back-btn" onClick={handleBack}>←</button>
                {activeGroup ? (
                  <>
                    <div className="widget-group-avatar"><Users size={18} /></div>
                    <div className="widget-chat-info">
                      <div className="widget-chat-name">{getGroupName(activeGroup)}</div>
                      <div className="widget-chat-status">{getMemberCount(activeGroup)} {t('messagesWidget.members')}</div>
                    </div>
                  </>
                ) : (() => {
                  const other = getOtherParticipant(activeConversation);
                  return (
                    <>
                      <img src={other?.avatar || other?.profileImage || '/default-avatar.png'}
                        alt={other?.organizationName || other?.name}
                        className="widget-chat-avatar" />
                      <div className="widget-chat-info">
                        <div className="widget-chat-name">{other?.organizationName || other?.name}</div>
                        <div className="widget-chat-status">{t('messagesWidget.online')}</div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Messages list */}
              <div className="widget-messages-container">
                {messages.map(message => {
                  const isSent = message.sender?._id === user._id || message.sender === user._id;
                  const isSharedPost = message.metadata?.type === 'shared_post' || message.metadata?.sharedPost;
                  const postId = message.metadata?.postId;
                  const senderName = activeGroup && !isSent ? getSenderName(message) : null;

                  return (
                    <div key={message._id} className={`widget-message ${isSent ? 'sent' : 'received'}`}>
                      {!isSent && (
                        <img
                          src={message.sender?.avatar || message.sender?.profileImage || '/default-avatar.png'}
                          alt={message.sender?.name}
                          className="widget-message-avatar"
                        />
                      )}
                      <div className="widget-message-content">
                        {senderName && <div className="widget-group-sender-name">{senderName}</div>}

                        {isSent && (
                          <button className="widget-delete-message-btn"
                            onClick={e => { e.stopPropagation(); handleDeleteMessage(message._id); }}
                            title={t('messagesWidget.delete')} type="button"><Trash2 size={14} /></button>
                        )}

                        {!isSharedPost && message.content && <p>{message.content}</p>}

                        {isSharedPost && (
                          <div className="widget-shared-post widget-shared-post-card"
                            onClick={() => postId && setOpenPostModalId(postId)}
                            role="button" tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && postId && setOpenPostModalId(postId)}>
                            <div className="shared-post-label">📤 {t('messagesWidget.sharedPost')}</div>
                            <div className="widget-shared-post-author">
                              {message.metadata.postAuthorImage && (
                                <img src={getImageUrl(message.metadata.postAuthorImage)} alt=""
                                  className="widget-shared-post-avatar"
                                  onError={e => { e.target.style.display = 'none'; }} />
                              )}
                              <span>{message.metadata.postAuthorName || t('messagesWidget.unknown')}</span>
                            </div>
                            {(message.metadata.postImage || message.metadata.postImages?.[0]) && (
                              <img
                                src={getImageUrl(message.metadata.postImage || message.metadata.postImages[0])}
                                alt="Post" className="widget-shared-post-img"
                                onError={e => e.target.style.display = 'none'} />
                            )}
                            <div className="shared-post-preview">
                              {message.metadata.postContent
                                ? `${message.metadata.postContent.substring(0, 80)}${message.metadata.postContent.length > 80 ? '...' : ''}`
                                : t('messagesWidget.post')}
                            </div>
                            <div className="widget-shared-post-tap">{t('messagesWidget.tapToView')}</div>
                          </div>
                        )}
                        <span className="widget-message-time" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {formatTime(message.createdAt)}
                          {isSent && (message.isRead
                            ? <CheckCheck size={12} color="#32614d" />
                            : <Check size={12} color="#94a3b8" />)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {sharePost && (
                  <div className="widget-shared-post-preview">
                    <div className="shared-post-header">
                      <span>{t('messagesWidget.sharingPost')}</span>
                      <button onClick={() => setSharePost(null)}><X size={14} /></button>
                    </div>
                    <div className="shared-post-content">
                      <p>{sharePost.content?.substring(0, 100)}...</p>
                      <small>{t('messagesWidget.from', { name: sharePost.author?.name || sharePost.author?.organizationName })}</small>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="widget-message-input">
                <button ref={emojiButtonRef} type="button" className="widget-emoji-btn"
                  onClick={() => setEmojiPickerOpen(v => !v)} title={t('messagesWidget.emojis')}>
                  <Smile size={18} />
                </button>
                <textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder={t('messagesWidget.typeMessage')}
                  rows="2"
                />
                <button onClick={sendMessage} disabled={!newMessage.trim() && !sharePost}
                  className="widget-send-btn">
                  <Send size={18} />
                </button>
                {emojiPickerOpen && <EmojiPicker variant="panel" />}
              </div>
            </>
          ) : (
            /* ══ List view ══════════════════════════════════════════ */
            <>
              {/* Tab bar */}
              <div className="widget-tab-bar">
                <button className={`widget-tab-btn ${activeTab === 'direct' ? 'active' : ''}`}
                  onClick={() => setActiveTab('direct')}>
                  <MessageSquare size={13} /> {t('messagesWidget.direct')}
                </button>
                <button className={`widget-tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
                  onClick={() => setActiveTab('groups')}>
                  <Users size={13} /> {t('messagesWidget.groups')}
                </button>
              </div>

              <div className="widget-conversations">
                {activeTab === 'direct' ? (
                  conversations.length === 0 ? (
                    <div className="widget-empty-state">
                      <p>{t('messagesWidget.noConversations')}</p>
                      <button className="widget-new-chat-btn" onClick={() => setShowNewChatModal(true)}>
                        {t('messagesWidget.startNewChat')}
                      </button>
                    </div>
                  ) : (
                    conversations.map(conv => {
                      const other = getOtherParticipant(conv);
                      const unread = conv.unreadCount || 0;
                      return (
                        <div key={conv._id}
                          className={`widget-conversation-item ${unread > 0 ? 'unread' : ''}`}
                          onClick={() => {
                            setActiveConversation(conv);
                            setActiveGroup(null);
                            markAsRead(conv._id);
                          }}>
                          <img src={other?.avatar || other?.profileImage || '/default-avatar.png'}
                            alt={other?.organizationName || other?.name}
                            className="widget-conv-avatar" />
                          <div className="widget-conv-info">
                            <div className="widget-conv-name">
                              {other?.organizationName || other?.name}
                              {unread > 0 && <span className="widget-unread-dot" />}
                            </div>
                            <div className="widget-conv-preview">
                              {conv.lastMessage?.content?.substring(0, 30) || t('messagesWidget.noMessages')}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  /* Groups list */
                  groups.length === 0 ? (
                    <div className="widget-empty-state">
                      <p>{t('messagesWidget.noGroups')}</p>
                      <button className="widget-new-chat-btn"
                        onClick={() => { navigate('/community'); setIsOpen(false); }}>
                        {t('messagesWidget.browseGroups')}
                      </button>
                    </div>
                  ) : (
                    groups.map(group => (
                      <div key={group._id} className="widget-conversation-item"
                        onClick={() => {
                          markGroupAsRead(group._id);
                          setActiveGroup(group);
                          setActiveConversation(null);
                          updateUnreadCount(conversations, groups);
                        }}>
                        <div className="widget-conv-group-avatar"><Users size={17} /></div>
                        <div className="widget-conv-info">
                          <div className="widget-conv-name">
                            {group.name}
                            {isGroupUnread(group, user?._id) && <span className="widget-unread-dot" />}
                          </div>
                          <div className="widget-conv-preview">
                            {group.lastMessage
                              ? `${group.lastMessage.sender?.name || t('messagesWidget.someone')}: ${group.lastMessage.content?.substring(0, 22) || ''}`
                              : (group.description?.substring(0, 30) || t('messagesWidget.noMessages'))}
                          </div>
                        </div>
                      </div>
                    ))
                  )
                )}
              </div>
            </>
          )}

          {/* New Chat Modal */}
          {showNewChatModal && (
            <div className="widget-modal-overlay" onClick={() => setShowNewChatModal(false)}>
              <div className="widget-modal" onClick={e => e.stopPropagation()}>
                <div className="widget-modal-header">
                  <h4>{t('messagesWidget.newMessageTitle')}</h4>
                  <button onClick={() => setShowNewChatModal(false)}><X size={18} /></button>
                </div>
                <div className="widget-modal-search">
                  <Search size={16} />
                  <input type="text" placeholder={t('messagesWidget.searchFollowed')}
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>
                <EmojiPicker variant="modal" />
                <div className="widget-modal-users">
                  {followedUsers
                    .filter(f => {
                      const u = f.user || f.followedUser || f;
                      const s = searchQuery.toLowerCase();
                      return u.name?.toLowerCase().includes(s) ||
                        u.organizationName?.toLowerCase().includes(s) ||
                        u.username?.toLowerCase().includes(s);
                    })
                    .map(f => {
                      const u = f.user || f.followedUser || f;
                      return (
                        <div key={u._id} className="widget-modal-user-item" onClick={() => startConversation(u)}>
                          <img src={u.avatar || u.profileImage || '/default-avatar.png'}
                            alt={u.organizationName || u.name} className="widget-modal-avatar" />
                          <div className="widget-modal-user-info">
                            <div className="widget-modal-user-name">{u.organizationName || u.name}</div>
                            <div className="widget-modal-user-type">
                              {u.userType === 'organizer' ? t('messagesWidget.organization') : t('messagesWidget.user')}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {followedUsers.length === 0 && (
                    <div className="widget-modal-empty">
                      <p>{t('messagesWidget.notFollowingAnyone')}</p>
                      <span>{t('messagesWidget.followToMessage')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {openPostModalId && (
        <PostModal postId={openPostModalId} isOpen={!!openPostModalId} onClose={() => setOpenPostModalId(null)} />
      )}
    </>
  );
}

export default MessagesWidget;
